import { useEffect, useState } from "react";
import { IEmployee } from "../types/employee";
import { checkIban } from "../utils/iban";
import { checkRrnr } from "../utils/rrnr";
import FormCategory from "./FormCategory";
import FormInput from "./FormInput";
import postCodes from "../utils/postcodes";
import countries from "../utils/countries";
import useStore from "../store/store";
import formatDateToIso from "../utils/formatDateToIso";
import { fetchBic } from "../utils/fetchBic";
const EmployeeForm = ({
  formData,
  setFormData,
}: {
  formData: IEmployee;
  setFormData: (empl: IEmployee) => void;
}) => {
  // used to check the rrnr and do fancy stuff like add . and -
  const [prevIbanLength, setPrevIbanLength] = useState(
    formData?.bankAccountNumber.length || 0
  );

  const { employees } = useStore();

  return (
    <>
      <div className="w-full">
        <FormCategory category="Algemeen"></FormCategory>

        <div className="mt-3.5 mb-12 flex gap-6 flex-wrap mx-3.5">
          <FormInput
            label="Voornaam (verplicht)"
            id="firstName"
            value={formData.firstName}
            setValue={(newValue) => {
              setFormData({ ...formData, firstName: newValue });
            }}
          ></FormInput>
          <FormInput
            label="Achternaam (verplicht)"
            id="lastName"
            value={formData.lastName}
            setValue={(newValue) => {
              setFormData({ ...formData, lastName: newValue });
            }}
          ></FormInput>

          <FormInput
            label="Rijksregisternummer (verplicht)"
            id="registerNumber"
            value={formData.registerNumber}
            setValue={(newValue) => {
              setFormData({ ...formData, registerNumber: newValue });
              checkRrnr(newValue, setFormData, formData);
            }}
            maxLength={15}
          ></FormInput>

          <div className="flex flex-col w-56">
            <label htmlFor="sex" className="pb-1">
              Geslacht (verplicht)
            </label>
            <select
              className="py-1 px-0 border-0 border-b text-xs"
              name="sex"
              id="sex"
              value={formData.sex}
              onChange={(e) =>
                setFormData({ ...formData, sex: e.target.value })
              }
              required
            >
              <option value="">Selecteer geslacht</option>

              <option value="Man">Man</option>
              <option value="Vrouw">Vrouw</option>
              <option value="Ander">Ander</option>
            </select>
          </div>

          <FormInput
            label="Geboortedatum"
            id="dateOfBirth"
            value={formatDateToIso(formData.dateOfBirth)}
            setValue={(newValue) => {
              setFormData({ ...formData, dateOfBirth: newValue });
            }}
            type="date"
          ></FormInput>

          <FormInput
            label="Geboortestad (verplicht)"
            id="birthCity"
            value={formData.birthCity}
            setValue={(newValue) => {
              setFormData({ ...formData, birthCity: newValue });
            }}
          ></FormInput>

          <div className="flex flex-col w-56">
            <label htmlFor="birthCountry" className="pb-1">
              Geboorteland (verplicht)
            </label>
            <select
              className="py-1 px-0 border-0 border-b text-xs"
              name="birthCountry"
              id="birthCountry"
              value={formData.birthCountry}
              onChange={(e) =>
                setFormData({ ...formData, birthCountry: e.target.value })
              }
              required
            >
              <option value="">Selecteer geboorteland</option>

              <option value="BE">België 🇧🇪</option>
              <option value="NL">Nederland 🇳🇱</option>
              <option value="FR">Frankrijk 🇫🇷</option>
              <option value="DE">Duitsland 🇩🇪</option>
              <option value="RO">Roemenië 🇷🇴</option>
              <option value="PL">Polen 🇵🇱</option>
              <option value="ENGLAND">Engeland 🏴󠁧󠁢󠁥󠁮󠁧󠁿</option>

              <option value="" disabled>
                --------
              </option>

              {countries
                .filter(
                  (c) =>
                    !["BE", "NL", "FR", "DE", "RO", "PL", "ENGLAND"].includes(
                      c.code
                    )
                )
                .map((c) => {
                  return (
                    <option key={c.code} value={c.code}>
                      {c.name} {c.emoji}
                    </option>
                  );
                })}
            </select>
          </div>

          <div className="flex flex-col w-56">
            <label htmlFor="nationality" className="pb-1">
              Nationaliteit (verplicht)
            </label>
            <select
              className="py-1 px-0 border-0 border-b text-xs"
              name="nationality"
              id="nationality"
              value={formData.nationality}
              onChange={(e) =>
                setFormData({ ...formData, nationality: e.target.value })
              }
              required
            >
              <option value="">Selecteer nationaliteit</option>

              <option value="BE">België 🇧🇪</option>
              <option value="NL">Nederland 🇳🇱</option>
              <option value="FR">Frankrijk 🇫🇷</option>
              <option value="DE">Duitsland 🇩🇪</option>
              <option value="RO">Roemenië 🇷🇴</option>
              <option value="PL">Polen 🇵🇱</option>
              <option value="ENGLAND">Engeland 🏴󠁧󠁢󠁥󠁮󠁧󠁿</option>

              <option value="" disabled>
                --------
              </option>

              {countries
                .filter(
                  (c) =>
                    !["BE", "NL", "FR", "DE", "RO", "PL", "ENGLAND"].includes(
                      c.code
                    )
                )
                .map((c) => {
                  return (
                    <option key={c.code} value={c.code}>
                      {c.name} {c.emoji}
                    </option>
                  );
                })}
            </select>
          </div>
        </div>
      </div>
      <div className="address">
        <FormCategory category="Adres"></FormCategory>

        <div className="mt-3.5 mb-12 flex gap-6 flex-wrap mx-3.5">
          <FormInput
            label="Straat (verplicht)"
            id="street"
            value={formData.street}
            setValue={(newValue) => {
              setFormData({ ...formData, street: newValue });
            }}
          ></FormInput>

          <FormInput
            label="Nummer (verplicht)"
            id="number"
            value={formData.number}
            setValue={(newValue) => {
              setFormData({ ...formData, number: newValue });
            }}
            type="text"
          ></FormInput>

          <div className="flex flex-col w-56">
            <label htmlFor="bus" className="pb-1">
              Bus
            </label>
            <input
              className="py-1 px-0 border-0 border-b text-xs"
              type="text"
              name="bus"
              id="bus"
              value={formData.bus}
              onChange={(e) =>
                setFormData({ ...formData, bus: e.target.value })
              }
            />
          </div>

          <FormInput
            label="Postcode (verplicht)"
            id="zip"
            value={formData.zip}
            setValue={(newValue) => {
              setFormData({ ...formData, zip: newValue });
            }}
            onBlur={() => {
              //if (!formData.city) {
              const data = postCodes.find(
                (pc) => pc.Postcode.toString() === formData.zip
              );

              if (data) {
                setFormData({
                  ...formData,
                  city:
                    data.Plaatsnaam === null
                      ? data.Hoofdgemeente
                      : data.Plaatsnaam,
                  country: "BE",
                });
              } else {
                setFormData({
                  ...formData,
                  city: "",
                  country: "",
                });
              }
              //}
            }}
          ></FormInput>
          <FormInput
            label="Stad (verplicht)"
            id="city"
            value={formData.city}
            setValue={(newValue) => {
              setFormData({ ...formData, city: newValue });
            }}
          ></FormInput>

          <div className="flex flex-col w-56">
            <label htmlFor="country" className="pb-1">
              Land (verplicht)
            </label>
            <select
              className="py-1 px-0 border-0 border-b text-xs"
              name="country"
              id="country"
              value={formData.country}
              onChange={(e) =>
                setFormData({ ...formData, country: e.target.value })
              }
              required
            >
              <option value="">Selecteer land</option>

              <option value="BE">België 🇧🇪</option>
              <option value="NL">Nederland 🇳🇱</option>
              <option value="FR">Frankrijk 🇫🇷</option>
              <option value="DE">Duitsland 🇩🇪</option>
              <option value="RO">Roemenië 🇷🇴</option>
              <option value="PL">Polen 🇵🇱</option>
              <option value="ENGLAND">Engeland 🏴󠁧󠁢󠁥󠁮󠁧󠁿</option>

              <option value="" disabled>
                --------
              </option>

              {countries
                .filter(
                  (c) =>
                    !["BE", "NL", "FR", "DE", "RO", "PL", "ENGLAND"].includes(
                      c.code
                    )
                )
                .map((c) => {
                  return (
                    <option key={c.code} value={c.code}>
                      {c.name} {c.emoji}
                    </option>
                  );
                })}
            </select>
          </div>
        </div>
      </div>
      <div className="contact">
        <FormCategory category="Contactgegevens"></FormCategory>

        <div className="mt-3.5 mb-12 flex gap-6 flex-wrap mx-3.5">
          <div className="flex flex-col w-56">
            <label htmlFor="language" className="pb-1">
              Taal (verplicht)
            </label>
            <select
              className="py-1 px-0 border-0 border-b text-xs"
              name="language"
              id="language"
              value={formData.language}
              onChange={(e) =>
                setFormData({ ...formData, language: e.target.value })
              }
              required
            >
              <option value="">Selecteer taal</option>
              <option value="NL">Nederlands</option>
              <option value="FR">Frans</option>
              <option value="EN">Engels</option>
            </select>
          </div>

          <FormInput
            label="Telefoonnummer (verplicht)"
            id="phone"
            value={formData.phone}
            setValue={(newValue) => {
              setFormData({ ...formData, phone: newValue });
            }}
            type="tel"
          ></FormInput>

          <FormInput
            label="E-mailadres (verplicht)"
            id="email"
            value={formData.email}
            setValue={(newValue) => {
              setFormData({ ...formData, email: newValue });
            }}
            type="email"
          ></FormInput>
        </div>
      </div>
      <div className="payment">
        <FormCategory category="Betalingsgegevens"></FormCategory>

        <div className="mt-3.5 mb-12 flex gap-6 flex-wrap mx-3.5">
          <div className="flex flex-col w-56">
            <label htmlFor="cevilRegistery" className="pb-1">
              Burgelijke stand
            </label>
            <select
              className="py-1 px-0 border-0 border-b text-xs"
              name="cevilRegistery"
              id="cevilRegistery"
              value={formData.cevilRegistery}
              onChange={(e) =>
                setFormData({ ...formData, cevilRegistery: e.target.value })
              }
              required
            >
              <option value="">Selecteer burgerlijke stand</option>
              {[
                "ongehuwd",
                "gehuwd",
                "weduwnaar(e)(naar)",
                "Feitelijk gescheiden",
                "Wettelijk gescheiden",
                "Gescheiden van tafel en bed",
                "Samenwonend",
                "Wettelijk samenwonend",
              ].map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col w-56">
            <label htmlFor="dependentSpouse" className="pb-1">
              Echtgeno(o)t(te) ten laste
            </label>
            <select
              className="py-1 px-0 border-0 border-b text-xs"
              name="dependentSpouse"
              id="dependentSpouse"
              value={formData.dependentSpouse}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  dependentSpouse: e.target.value,
                })
              }
              required
            >
              <option value="">Selecteer</option>
              <option value="Geen">Geen</option>
              <option value="Met inkomsten">Met inkomsten</option>
              <option value="Zonder inkomsten">Zonder inkomsten</option>
            </select>
          </div>

          <FormInput
            label="Kinderen ten laste"
            id="dependentChildren"
            value={formData.dependentChildren}
            setValue={(newValue) => {
              setFormData({ ...formData, dependentChildren: newValue });
            }}
            type="number"
            min={0}
          ></FormInput>

          <FormInput
            label="Rekeningnummer (IBAN) (verplicht)"
            id="bankAccountNumber"
            value={formData.bankAccountNumber}
            setValue={(newValue) => {
              setFormData({ ...formData, bankAccountNumber: newValue });
              checkIban(newValue, prevIbanLength, setFormData, formData);
              setPrevIbanLength(newValue.length);
            }}
            onBlur={() => {
              fetchBic(setFormData, formData);
            }}
            maxLength={19}
          ></FormInput>
          <FormInput
            label="BIC (automatisch)"
            id="bic"
            disabled
            value={formData.bic}
            setValue={(bic) => {
              setFormData({ ...formData, bic });
            }}
            maxLength={19}
          ></FormInput>
        </div>
      </div>
      <div className="extraInfo">
        <FormCategory category="Extra info"></FormCategory>

        <div className="mt-3.5 pb-12 flex-col mx-3.5">
          <div className="mt-3.5 mb-12 flex gap-6 flex-wrap">
            <div className="flex flex-col w-56">
              <label htmlFor="status" className="pb-1">
                Statuut (verplicht)
              </label>
              <select
                className="py-1 px-0 border-0 border-b text-xs"
                name="status"
                id="status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                required
              >
                <option value="">Selecteer statuut</option>
                {[
                  "Arbeider",
                  "Bediende",
                  "Arbeider Jobstudent",
                  "Seizoensarbeider",
                  "Bediende Jobstudent",
                  "Flexijob Arbeider",
                  "Flexijob Bediende",
                  "Werkstudent Arbeider",
                  "Werkstudent Bediende",
                  "Gelegenheidsarbeider",
                ].map((op) => (
                  <option key={op} value={op}>
                    {op}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col w-56">
              <label htmlFor="permanentEmployee" className="pb-1">
                Vaste medewerker
              </label>
              <input
                className="my-1 px-0 border-2 rounded-sm"
                type="checkbox"
                name="permanentEmployee"
                id="permanentEmployee"
                checked={formData.permanentEmployee}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    permanentEmployee: e.target.checked,
                  })
                }
              />
            </div>
          </div>
          <div className="flex flex-col w-full">
            <label htmlFor="paymentInfo" className="pb-1">
              Loonsinfo (verplicht)
            </label>
            <textarea
              className="py-1 px-0 border-0 border-b text-xs w-full resize-none"
              name="paymentInfo"
              id="paymentInfo"
              rows={10}
              value={formData.paymentInfo}
              onChange={(e) =>
                setFormData({ ...formData, paymentInfo: e.target.value })
              }
              required
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default EmployeeForm;
